/**
 * Keeps shared-itinerary tokens out of Sentry.
 *
 * A share token is a bearer credential: anyone holding `/share/<token>` can read the itinerary. Sentry
 * collects the page URL in many places — the error's request URL (HTTP-context integration), the
 * pageload/navigation transaction name, span attributes, navigation and fetch breadcrumbs, and console
 * logs — so redacting one field is not enough. `scrubShareTokens` walks a whole Sentry payload and
 * rewrites every `/share/<token>` it finds to `/share/:token` (the same form GA4 receives, see
 * `redactAnalyticsUrl`). Session replay is handled separately in `sentry.ts`: it is not started on
 * share pages, and the owner's share dialog masks the link.
 */

/** A `/share/` path segment carrying a token (base64url, 32 chars today; 16+ to be safe). */
const SHARE_TOKEN_PATTERN = /(\/share\/)[\w-]{16,}/g;

/** `/share/<token>` → `/share/:token` anywhere in `text`. */
export function scrubShareTokensInText(text: string): string {
  return text.replaceAll(SHARE_TOKEN_PATTERN, '$1:token');
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  const prototype = Object.getPrototypeOf(value) as unknown;
  return prototype === Object.prototype || prototype === null;
}

/**
 * Returns a copy of `value` (a Sentry event, transaction, span, breadcrumb or log) with every share
 * token redacted. Walks plain objects and arrays only; strings without a token, numbers and any
 * non-plain objects are returned as they are, so the payload's shape never changes.
 *
 * @example
 * scrubShareTokens({ request: { url: 'https://app.elara.travel/share/abcdefghijklmnopqrstuvwxyz012345' } });
 * // { request: { url: 'https://app.elara.travel/share/:token' } }
 */
export function scrubShareTokens<T>(value: T): T {
  if (typeof value === 'string') return scrubShareTokensInText(value) as T;
  if (Array.isArray(value)) return value.map((item: unknown) => scrubShareTokens(item)) as T;
  if (!isPlainObject(value)) return value;
  const scrubbed: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value)) scrubbed[scrubShareTokensInText(key)] = scrubShareTokens(entry);
  return scrubbed as T;
}

/** Whether the current page is a public shared itinerary (where session replay is never started). */
export function isSharedItineraryPath(pathname: string): boolean {
  return pathname.startsWith('/share/');
}
